import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllConversationOfCompanyWithWorker } from "../../services/common/chatApiFn";
import appcolors from "../../constants/Colors";
import moment from "moment";
import { setSelectedChat } from "../../lib/slices/selectedChatSlice";
import { t } from "i18next";

function AllWorkers({ socket }) {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.userData.user);
  const [conversations, setConversations] = useState(null);
  const [loading, setLoading] = useState(false); // Added loading state
  const [error, setError] = useState(false); // Added error state
  const active = useSelector((state) => state.activeChat.selectedChat);

  useEffect(() => {
    if (userData) {
      fetchCompanyConversationWithWorker();
    }
  }, [userData]);

  useEffect(() => {
    if (socket) {
      socket.emit("joinCompanyRoom", { companyId: userData?.adminId });

      const handleNewConversation = (newConversation) => {
        console.log("NEW", newConversation);

        setConversations((prev) => {
          const updatedIndex = prev.findIndex(
            (company) =>
              company.conversationId === newConversation.conversationId
          );

          if (updatedIndex > -1) {
            // Update existing conversation
            const updatedCompanies = [...prev];
            updatedCompanies[updatedIndex].lastMessage =
              newConversation.lastMessage;
            updatedCompanies[updatedIndex].lastUpdated =
              newConversation.lastUpdated;
            updatedCompanies[updatedIndex].unreadMessagesCount =
              newConversation.unreadMessagesCount;

            // Move updated conversation to the top
            const [updatedCompany] = updatedCompanies.splice(updatedIndex, 1);
            return [updatedCompany, ...updatedCompanies];
          } else {
            const user = {};
            newConversation.participants?.forEach((participant) => {
              const { role, userId } = participant;
              user[role] = { ...userId, role };
            });

            const transformedConversation = {
              ...newConversation,
              user,
            };

            return [transformedConversation, ...prev];
          }
        });
      };

      socket.on("newConversationWithWorker", handleNewConversation);

      // Cleanup function to avoid memory leaks
      return () => {
        socket.off("newConversationWithWorker", handleNewConversation);
        socket.emit("leaveRoom", { companyId: userData?.adminId });
      };
    }
  }, [socket, conversations]);

  const fetchCompanyConversationWithWorker = async () => {
    try {
      setLoading(true);
      const response = await getAllConversationOfCompanyWithWorker(
        userData?.adminId
      );
      if (response.data.status === "success") {
        const data = response.data.data;

        setConversations(transformedData(data));
        setLoading(false);
      }
    } catch (error) {
      setError(true);
      setLoading(false);
    }
  };

  const transformedData = (data) => {
    return data?.map((conversation) => {
      const user = {};

      conversation.participants.forEach((participant) => {
        const { role, userId } = participant;
        user[role] = { ...userId, role };
      });

      return {
        ...conversation,
        user,
      };
    });
  };

  if (loading) {
    return (
      <Stack
        p={"6px 6px"}
        borderRadius={2}
        border={"1px solid #615EF0"}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        sx={{ cursor: "default" }}
      >
        <Skeleton variant="circular" width={48} height={48} />
        <Stack flex={1} ml={2}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="80%" />
        </Stack>
      </Stack>
    );
  }

  if (error || !conversations) {
    return (
      <Stack
        p={"6px 6px"}
        borderRadius={2}
        border={"1px solid #615EF0"}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        sx={{ cursor: "default" }}
      >
        <Typography
          fontSize={"0.875rem"}
          fontWeight={"bold"}
          color="error"
          width="100%"
          textAlign="center"
        >
          {t("somethingWentWrong")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Display loading spinner if data is being fetched */}
      {loading ? (
        <CircularProgress size={24} sx={{ alignSelf: "center" }} />
      ) : loading === false && conversations?.length === 0 ? (
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{
            textAlign: "center",
            padding: "20px",
            backgroundColor: "#f9f9f9",
            borderRadius: "8px",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="h6" color={appcolors.primary} fontWeight="bold">
            {t("noWorkersFound")}
          </Typography>
          <Typography variant="body2" color={appcolors.lightGrey}>
            {t("noWorkersMessage")}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => fetchCompanyConversationWithWorker()}
            sx={{ marginTop: "10px" }}
          >
            {t("refresh")}
          </Button>
        </Stack>
      ) : (
        <List
          sx={{
            height: "40vh",
            overflowX: "auto",
          }}
        >
          {conversations
            ?.sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated))
            ?.map((val, index) => (
              <ListItem
                key={index}
                divider
                sx={{
                  py: 0,
                  cursor: "pointer",
                  backgroundColor:
                    val._id == active?._id ? "#615EF00F" : "transparent",
                  "&:hover": {
                    backgroundColor: "#615EF00F",
                  },
                }}
                onClick={() =>
                  dispatch(
                    setSelectedChat({
                      _id: val?._id,
                      name: val.user.Worker.name,
                      receiverId: val.user.Worker._id,
                      image: val.user.Worker.profileImg,
                      conversationId: val.conversationId,
                      senderRole: "Company",
                      receiverRole: "Worker",
                      type: "CompanyWithWorker",
                    })
                  )
                }
              >
                <ListItemAvatar>
                  <Avatar
                    src={val?.user?.Worker?.profileImg || "/default-avatar.png"}
                    sx={{ borderRadius: "20%" }}
                  />
                </ListItemAvatar>
                <ListItemText
                  sx={{ mt: "0px !important" }}
                  primary={
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                      width={"100%"}
                    >
                      <Typography
                        variant="body1"
                        fontWeight={"bold"}
                        flex={4}
                        noWrap
                      >
                        {val?.user?.Worker?.name}
                      </Typography>
                      <Typography fontSize={"0.75rem"} noWrap>
                        {moment(val?.lastUpdated).format("hh:mm a")}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <Stack
                      gap={0.5}
                      direction={"row"}
                      justifyContent={"space-between"}
                    >
                      <Typography variant="body2" color={"#7c7c7c"} noWrap>
                        {val?.lastMessage || t("noMessagesYet")}
                      </Typography>

                      {val?.unreadMessagesCount[userData?.adminId] > 0 && (
                        <Chip
                          label={
                            val.unreadMessagesCount[userData?.adminId] || 0
                          }
                          sx={{
                            color: "white",
                            height: "auto",
                            backgroundColor: "primary.main",
                            "& .MuiChip-label": {
                              padding: "2px 6px 2px 6px",
                            },
                          }}
                        />
                      )}
                    </Stack>
                  }
                />
              </ListItem>
            ))}
        </List>
      )}
    </Stack>
  );
}

export default AllWorkers;
